import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, filter, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';

import { NavRoutes } from '../scootaverse-page/constants';
import { BreadcrumbNavigation } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type queryOptionType = Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class ScootaverseRoutingService extends Loggable implements OnDestroy {
  private currentNavState: BreadcrumbNavigation[] = [];
  private destroyed$ = new ReplaySubject<boolean>(1);

  private pageNavigationStateSubject = new BehaviorSubject<BreadcrumbNavigation[]>([]);
  public pageNavigationState$ = this.pageNavigationStateSubject.asObservable();

  private navRoutes = NavRoutes;

  public constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    super();
    this.setDisplayName('ScootaverseRoutingService');

    this.pageNavigationState$.pipe(takeUntil(this.destroyed$)).subscribe((navState) => {
      this.currentNavState = navState;
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed$),
      )
      .subscribe((event) => {
        this.formatCurrentRoute(event as NavigationEnd);
      });
  }

  private getUrlTreeFromActualRoute(url: string): string[] | undefined {
    const navRoute = this.navRoutes.find((nav) =>
      this.urlChecker(
        this.formatUrlWithoutParams(url),
        nav.url,
        nav.urlActivationParams || [],
        nav.requireParameters || [],
      ),
    );

    if (navRoute) {
      return navRoute.urlTree;
    }

    let foundSubRoute: string[] | undefined = undefined;

    this.navRoutes.forEach((route) => {
      const subRoute = route.subRoutes?.find((sub) =>
        this.urlChecker(
          this.formatUrlWithoutParams(url),
          sub.url,
          sub.urlActivationParams || [],
          sub.requireParameters || [],
        ),
      );

      if (subRoute) {
        foundSubRoute = subRoute.urlTree;
      }
    });

    if (foundSubRoute) {
      return foundSubRoute;
    }
    return undefined;
  }

  private getRouteByName(name: string): { name?: string; url?: string; urlTree?: string[] } | undefined {
    const navRoute = this.navRoutes.find((nav) => {
      return nav.name === name;
    });

    if (navRoute) {
      return { name: navRoute.name, url: navRoute.url, urlTree: navRoute.urlTree };
    }

    let foundSubRoute: { name?: string; url?: string; urlTree?: string[] } | undefined = undefined;

    this.navRoutes.forEach((route) => {
      const subRoute = route.subRoutes?.find((sub) => {
        return sub.name === name;
      });

      if (subRoute) {
        foundSubRoute = {
          name: subRoute.name,
          url: subRoute.url,
          urlTree: subRoute.urlTree,
        };
      }
    });

    if (foundSubRoute) {
      return foundSubRoute;
    }
    return undefined;
  }

  private formatCurrentRoute(event: NavigationEnd): void {
    const navStateCrumb: BreadcrumbNavigation[] = [];
    const actualRouteTree = this.getUrlTreeFromActualRoute(event.url);

    if (actualRouteTree) {
      actualRouteTree.forEach((urlItem) => {
        const navItem = this.getRouteByName(urlItem);
        navStateCrumb.push({ title: urlItem, nav: navItem });
      });
    }

    this.pageNavigationStateSubject.next(navStateCrumb);
  }

  /** Check if a URL should be active, based on the actual URL
   * @fullURL: Actual URL
   * @activationURL: URL to be compared
   * @keys: URL key strings to be verified
   * @requiredParameters: Parameter names required by the page for it to be considered active
   * @parameters: Parameter values by the page for it to be considered active
   */
  public urlChecker(
    fullUrl: string,
    activationUrl: string,
    keys: string[],
    requiredParameters?: string[],
    parameters?: queryOptionType,
  ): boolean {
    let urlMatch = false;
    const parameterSize = keys.length;
    const requiredParametersSize = keys.length;

    if (fullUrl) {
      // If activationURL is the same as the actual url, it's active
      if (activationUrl === fullUrl) {
        return true;
      }
      // Compare the URLs by the activation keys defined in the constants file
      if (parameterSize) {
        urlMatch = true;
        // Divide URL and remove ids
        const urlSplits = fullUrl.split('/').filter((part) => part !== '' && !/\d/.test(part));

        // Check if all the items in the key array are in the URL
        for (const keyItem of keys) {
          if (urlSplits.indexOf(keyItem) === -1) {
            urlMatch = false;
          }
        }
        // Check if all the URL parts are present in the keys array as well
        for (const urlSplit of urlSplits) {
          if (keys.indexOf(urlSplit) === -1) {
            urlMatch = false;
          }
        }

        // If matched, check for required parameters
        if (urlMatch) {
          let hasParameters = false;
          if (requiredParameters && parameters && requiredParametersSize) {
            hasParameters = true;
            requiredParameters.forEach((parameter) => {
              switch (parameter) {
                case 'id':
                  if (!parameters.scootaverseId) {
                    hasParameters = false;
                  }
                  break;
                case 'room-id':
                  if (!parameters.socialId) {
                    hasParameters = false;
                  }
                  break;
              }
            });

            // If parameters are present, do a last check with the last URL split
            if (hasParameters) {
              if (keys[keys.length] !== fullUrl.split('/')[-1]) {
                return false;
              }
            } else {
              return false;
            }
          }
        }
      }
    }
    return urlMatch;
  }

  public formatUrlWithoutParams(url: string): string {
    const index = url.indexOf('?'); // Find the first digit in the string
    if (index !== -1) {
      return url.substring(0, index); // Extract the portion before the digit
    } else {
      return url; // Return the original string if no digit is found
    }
  }

  public async navigateWithParameters(
    currentRoute: string,
    parameters: string[],
    parametersValues: queryOptionType,
  ): Promise<string> {
    let finalRoute = currentRoute;
    //Get the parameters from the URL
    if (parameters.length) {
      //Replace the parameters based on their identification
      parameters.forEach((parameter) => {
        finalRoute = finalRoute.replace(`:${parameter}`, parametersValues[parameter]);
      });
    }
    await this.router.navigate([finalRoute]);
    return finalRoute;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.pageNavigationStateSubject.next([]);
  }
}
